/**
 * ─── VENDORED FROM arrow-fe ────────────────────────────────────────────────
 *
 * Source of truth: `arrow-fe/src/lib/crypto/upload-frame.ts`.
 *
 * This is a copy, not a fork. The panel speaks the same sealed-envelope
 * protocol as the storefront (docs/backend/ENCRYPTION.md), and two
 * implementations of a security protocol that drift is a real liability — so
 * changes belong upstream first, then get copied here.
 *
 * If this ever needs to diverge from the storefront's copy, that is the signal
 * to stop copying and extract a shared workspace package instead
 * (ADMIN.md §4). Do not "fix" it here in isolation.
 * ───────────────────────────────────────────────────────────────────────────
 */

/**
 * The binary upload frame — the browser side of
 * `arrow-be/src/lib/crypto/frame.ts`.
 *
 * An image cannot ride inside a JSON envelope: base64 costs a third of its size
 * again, and the API parses JSON at a 100kb limit. So an upload seals one
 * buffer holding both the file and its metadata:
 *
 *     ┌────────────┬──────────────────┬─────────────────────────────────────┐
 *     │ uint32be   │ metadata JSON    │ file bytes                          │
 *     │ metaLength │ (metaLength B)   │ (everything remaining)              │
 *     └────────────┴──────────────────┴─────────────────────────────────────┘
 *
 * One frame, one AES-GCM operation, one tag over all of it. The filename and
 * content type end up *inside* the ciphertext, so a captured upload does not
 * reveal what the file was called — which a multipart body would, in the clear,
 * in the request headers.
 *
 * ── Why anything else about the file belongs here too ──────────────────────
 *
 * The metadata blob is the only channel an upload has for saying anything
 * alongside its bytes. The body is the frame, so it cannot also be JSON; and a
 * query parameter is outside the ciphertext, which means it is neither hidden
 * nor covered by the tag — a caption could be rewritten in flight and the
 * server would not know. So descriptive fields go in here, sealed with the
 * bytes they describe.
 */

/** See the note on `Bytes` in `./envelope.ts`. */
type Bytes = Uint8Array<ArrayBuffer>;

const LENGTH_PREFIX_BYTES = 4;

export type UploadMetadata = {
  filename: string;
  contentType: string;
  /**
   * Alt text, where the upload is a picture that will be displayed.
   *
   * Optional in the frame because the frame is generic plumbing, and required
   * by the route that receives it — arrow-be refuses a product image without
   * one. See the note above for why it travels here rather than in the query
   * string.
   */
  alt?: string;
};

export function encodeFrame(metadata: UploadMetadata, bytes: Uint8Array): Bytes {
  const json = new TextEncoder().encode(JSON.stringify(metadata));
  const frame = new Uint8Array(LENGTH_PREFIX_BYTES + json.length + bytes.length);

  new DataView(frame.buffer).setUint32(0, json.length, false);
  frame.set(json, LENGTH_PREFIX_BYTES);
  frame.set(bytes, LENGTH_PREFIX_BYTES + json.length);

  return frame;
}

/**
 * Reads a picked `File` into a frame.
 *
 * The whole file lands in memory, unavoidably: AES-GCM cannot produce a tag
 * without seeing every byte, so there is no streaming version of this. Keep the
 * file picker's `accept` narrow and check `file.size` before calling — the
 * server's `UPLOAD_MAX_BYTES` will refuse an oversized upload, but only after
 * the browser has spent the upload doing it.
 */
export async function frameFromFile(
  file: File,
  extra: Omit<UploadMetadata, "filename" | "contentType"> = {},
): Promise<Bytes> {
  return encodeFrame(
    {
      ...extra,
      filename: file.name,
      // The server re-sniffs this from the magic bytes and refuses a mismatch,
      // so a browser that guesses badly gets a clear error rather than a file
      // stored under the wrong type.
      contentType: file.type,
    },
    new Uint8Array(await file.arrayBuffer()),
  );
}
